import type { IconKey } from '@cheqroom/icons';

import { FieldPath, Fields } from '../../@types/field-path.d';
import type { AssetURLs, Attachment } from '../attachments';
import type { Category } from '../categories';
import type { Checkout } from '../checkouts';
import type { Comment } from '../comments';
import type { Customer } from '../customers';
import type { Kit } from '../kits';
import type { Location } from '../locations';

export type ItemKind = 'simple' | 'bulk';

export type ItemStatus =
	| 'available'
	| 'checkedout'
	| 'await_checkout'
	| 'in_transit'
	| 'maintenance'
	| 'repair'
	| 'inspection'
	| 'expired'
	| 'in_custody';

export type ItemAvailabilityStatus =
	| 'available'
	| 'unavailable_status'
	| 'unavailable_flag'
	| 'unavailable_allow'
	| 'unavailable_other';

export interface OrderQuantityWithKit {
	orderId: string;
	quantity: number;
	kitId?: string;
}

export interface Item {
	_id: string;
	category: Category;
	flag?: string;
	cover?: string | null;
	cover_url?: AssetURLs;
	barcodes: string[];
	fields: Record<string, string>;
	created: string;
	modified: string | null;
	name: string;
	codes: string[];
	rfidcodes: string[];
	status: ItemStatus;
	kind: ItemKind;
	itemTypeId: string;
	consumable: boolean;
	quantity: number;
	brand: string | null;
	model: string | null;
	warrantyDate: string | null;
	purchaseDate: string | null;
	purchasePrice: number | null;
	residualValue: number | null;
	depreciatedValue: number | null;
	address: string | null;
	expired: string | null;
	location: Location;
	locationPath?: Array<Location>;
	custody?: Customer;
	allowReserve: boolean;
	allowOrder: boolean;
	allowCustody: boolean;
	canReserve: ItemAvailabilityStatus;
	canOrder: ItemAvailabilityStatus;
	canCustody: ItemAvailabilityStatus;
	kitQuantities?: Record<string, number>;
	inKits?: number;
	isRestricted: boolean;
	kit?: Kit;
	/**
	 * @deprecated use orderQuantitiesWithKits instead
	 * @see orderQuantitiesWithKits
	 */
	order?: Checkout;
	/**
	 * @deprecated use orderQuantitiesWithKits instead
	 * @see orderQuantitiesWithKits
	 */
	orders?: Record<string, number>;
	orderQuantitiesWithKits?: OrderQuantityWithKit[];
	comments?: Comment[];
	attachments?: Attachment[];
}

export enum ItemConflict {
	AVAILABLE = 'AVAILABLE',
	ASSIGNED = 'ASSIGNED',
	CHECKED_OUT = 'CHECKED_OUT',
	CHECKING_OUT = 'CHECKING_OUT',
	RETIRED = 'RETIRED',
	FLAGGED = 'FLAGGED',
	RESERVED = 'RESERVED',
	UNAVAILABLE = 'UNAVAILABLE',
	CANNOT_RESERVE = 'CANNOT_RESERVE',
	CANNOT_CHECKOUT = 'CANNOT_CHECKOUT',
	AT_WRONG_LOCATION = 'AT_WRONG_LOCATION',
	UNAVAILABLE_QUANTITY = 'UNAVAILABLE_QUANTITY',
	INSUFFICIENT_QUANTITY = 'INSUFFICIENT_QUANTITY',
	RESTRICTED = 'RESTRICTED',
	UNAVAILABLE_TO_ADD_TO_KIT = 'UNAVAILABLE_TO_ADD_TO_KIT',
	INCOMPLETE_KIT = 'INCOMPLETE_KIT',
	INCOMPLETE_KIT_AVAILABILITY = 'INCOMPLETE_KIT_AVAILABILITY',
	BUFFER_TIME = 'BUFFER_TIME',
	UNSCANNED = 'UNSCANNED',
	MISSING = 'MISSING',
	UNEXPECTED = 'UNEXPECTED',
}

export type ConflictItem = {
	id: string;
	name: string;
	image: string;
	kind: 'BULK' | 'SIMPLE';
};
interface ItemTypeSystemFieldsVisibility {
	brand: boolean;
	model: boolean;
	warranty_date: boolean;
	purchase_date: boolean;
	purchase_price: boolean;
	residual_value: boolean;
}

interface ItemTypeCustomFieldVisibility {
	field_def_id: string;
	visible: boolean;
}

interface ItemTypeSettings {
	allow_reserve: boolean;
	allow_order: boolean;
	allow_custody: boolean;
}

export interface ItemType {
	id: string;
	name: string;
	name_plural: string;
	description: string | null;
	icon: IconKey | null;
	is_default: Readonly<boolean>;
	system_fields_visibility: ItemTypeSystemFieldsVisibility;
	custom_field_visibility: ItemTypeCustomFieldVisibility[];
	settings: ItemTypeSettings;
	created: Readonly<string>;
	modified: Readonly<string>;
}

export type CustomFieldKey = `fields.${string}`;

export const SystemFields = [
	'name',
	'cover',
	'cover_url',
	'itemTypeId',
	'purchaseDate',
	'created',
	'modified',
	'retired',
	'model',
	'residualValue',
	'warrantyDate',
	'purchasePrice',
	'quantity',
	'user',
	'custody',
	'location',
	'category',
	'brand',
	'flag',
	'codes',
	'barcodes',
	'rfidcodes',
	'kit',
	'address',
	'isRestricted',
] as const;
export type SystemField = (typeof SystemFields)[number];

export type VirtualField = 'sublocation';

export type Field = SystemField | CustomFieldKey | VirtualField;

export const ITEM_SYSTEM_FIELDS = [
	{
		id: 'name',
		name: 'Name',
		kind: 'string',
		editor: 'text',
		unit: null,
		basicField: true,
	},
	{
		id: 'itemTypeId',
		name: 'Item type',
		kind: 'string',
		editor: 'text',
		unit: null,
		basicField: true,
		excludeFromFilters: true,
	},
	{
		id: 'purchaseDate',
		name: 'Purchase date',
		kind: 'datetime',
		editor: 'date',
		unit: null,
		basicField: true,
	},
	{
		id: 'warrantyDate',
		name: 'Warranty date',
		kind: 'datetime',
		editor: 'date',
		unit: null,
		basicField: true,
	},
	{
		id: 'created',
		name: 'Created',
		kind: 'datetime',
		editor: 'date',
		unit: null,
		basicField: true,
	},
	{
		id: 'modified',
		name: 'Last modified',
		kind: 'datetime',
		editor: 'date',
		unit: null,
		basicField: true,
	},
	{
		id: 'retired',
		name: 'Retired',
		kind: 'datetime',
		editor: 'date',
		unit: null,
		basicField: true,
	},
	{
		id: 'model',
		name: 'Model',
		kind: 'string',
		editor: 'text',
		unit: null,
		basicField: true,
	},
	{
		id: 'purchasePrice',
		name: 'Purchase price',
		kind: 'float',
		editor: 'currency',
		unit: null,
		basicField: true,
	},
	{
		id: 'residualValue',
		name: 'Residual value',
		kind: 'float',
		editor: 'currency',
		unit: null,
		basicField: true,
	},
	{
		id: 'depreciatedValue',
		name: 'Current value',
		kind: 'float',
		editor: 'currency',
		unit: null,
		basicField: true,
	},
	{
		id: 'quantity',
		name: 'Quantity',
		kind: 'int',
		editor: 'number',
		unit: null,
		basicField: true,
	},
	{
		id: 'custody',
		name: 'User',
		kind: 'string',
		editor: 'text',
		unit: null,
		basicField: true,
	},
	{
		id: 'location',
		name: 'Location',
		kind: 'string',
		editor: 'text',
		unit: null,
		basicField: true,
		excludeFromFilters: true,
	},
	{
		id: 'category',
		name: 'Category',
		kind: 'string',
		editor: 'text',
		unit: null,
		basicField: true,
		excludeFromFilters: true,
	},
	{
		id: 'brand',
		name: 'Brand',
		kind: 'string',
		editor: 'text',
		unit: null,
		basicField: true,
	},
	{
		id: 'flag',
		name: 'Flag',
		kind: 'string',
		editor: 'text',
		unit: null,
		basicField: true,
		excludeFromFilters: true,
	},
	{
		id: 'codes',
		name: 'QR',
		kind: 'string',
		editor: 'text',
		unit: null,
		basicField: true,
		allowedComparators: ['contains', 'starts_with', 'ends_with', 'empty', 'not_empty'],
	},
	{
		id: 'barcodes',
		name: 'Barcode',
		kind: 'string',
		editor: 'text',
		unit: null,
		basicField: true,
		allowedComparators: ['contains', 'starts_with', 'ends_with', 'empty', 'not_empty'],
	},
	{
		id: 'rfidcodes',
		name: 'RFID code',
		kind: 'string',
		editor: 'text',
		unit: null,
		basicField: true,
		allowedComparators: ['contains', 'starts_with', 'ends_with', 'empty', 'not_empty'],
	},
	{
		id: 'address',
		name: 'Last known position',
		kind: 'string',
		editor: 'text',
		unit: null,
		basicField: true,
	},
	{
		id: 'kit',
		name: 'Kit',
		kind: 'string',
		editor: 'text',
		unit: null,
		basicField: true,
		excludeFromFilters: true,
	},
	{
		id: 'isRestricted',
		name: 'Access',
		kind: 'boolean',
		editor: 'boolean',
		unit: null,
		excludeFromFilters: true,
	},
] as const;

export type ItemQueryField = FieldPath<Item> | keyof Item | CustomFieldKey | VirtualField;
export type ItemQueryData<F extends ItemQueryField = ItemQueryField> = Fields<
	Item,
	Extract<F, FieldPath<Item> | keyof Item>
>;
